import { useEffect, useState } from 'react';
import { Control, UseFormSetValue } from 'react-hook-form';
import { TextInputField } from '@brighte/form-components';
import { RepaymentAccountType } from '@brighte/validation';
import { datadogRum } from '@datadog/browser-rum';
import { CardPaymentMethod } from '@graphql/index';
import { useApplicationData } from '@provider';
import { Box } from '@spark-web/box';
import { Columns } from '@spark-web/columns';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Spinner } from '@spark-web/spinner';
import { Text } from '@spark-web/text';
import { TextLinkButton } from '@spark-web/text-link';
import { useDisclosure } from '@spark-web/utils';
import { LABELS } from '@utils';
import { useGetPaymentIFrameUrl } from './hooks';

type Props = {
  control: Control<RepaymentAccountType>;
  setValue: UseFormSetValue<RepaymentAccountType>;
};

function CreditCard({ control, setValue }: Props) {
  const { CREDIT_CARD_HOLDER, CREDIT_CARD_NUMBER, EXPIRY_DATE } = LABELS;
  const { isOpen, onOpen, onToggle } = useDisclosure();
  const { iframeUrl, isLoading } = useGetPaymentIFrameUrl({ onSuccess: onOpen });
  const { PaymentDetails: paymentDetails } = useApplicationData(['PaymentDetails']);
  const creditCardDetails = paymentDetails?.payment?.creditCard as Omit<CardPaymentMethod, 'id' | 'type'>;

  const [isCreditCardDetailsAvailable, setIsCreditCardDetailsAvailable] = useState(
    !!(paymentDetails?.payment.methodId && creditCardDetails?.token),
  );

  useEffect(() => {
    if (isCreditCardDetailsAvailable) {
      return;
    }

    const handleMessage = event => {
      const responseData = event.data;
      if (typeof responseData === 'string' && responseData.includes('"responseText":"SUCCESS"')) {
        const { cardHolder, cardNumber, cardExpiry, token, cardType } = JSON.parse(responseData);
        setValue('payment.creditCard.cardHolder', cardHolder);
        setValue('payment.creditCard.cardNumber', cardNumber);
        setValue('payment.creditCard.cardExpiry', cardExpiry);
        setValue('payment.creditCard.cardType', cardType);
        setValue('payment.creditCard.token', token);
        onToggle();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleCloseDialog = () => {
    if (paymentDetails?.payment.methodId) {
      setValue('payment.creditCard.token', creditCardDetails?.token ?? '');
      setValue('payment.creditCard.cardType', creditCardDetails?.cardType ?? '');
      setIsCreditCardDetailsAvailable(true);
      onToggle();
    } else {
      onToggle();
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" flex={1} data-testid="iframe-loader">
        <Spinner size="xsmall" tone="neutral" />
      </Box>
    );
  }

  return (
    <>
      {!isCreditCardDetailsAvailable && (
        <ContentDialog title="Credit card details" isOpen={isOpen} onToggle={handleCloseDialog} size="xsmall">
          <iframe
            src={iframeUrl}
            style={{
              width: '100%',
              height: '330px',
              border: 'none',
              padding: 0,
            }}
            data-testid="repayment-credit-card-iframe"
          />
        </ContentDialog>
      )}
      <TextInputField
        control={control}
        name="payment.creditCard.cardHolder"
        label={CREDIT_CARD_HOLDER}
        FieldProps={{ disabled: true }}
      />
      <Columns gap="xxlarge" collapseBelow="tablet">
        <TextInputField
          control={control}
          name="payment.creditCard.cardNumber"
          label={CREDIT_CARD_NUMBER}
          autoComplete="off"
          FieldProps={{ disabled: true }}
        />
        <TextInputField
          control={control}
          name="payment.creditCard.cardExpiry"
          label={EXPIRY_DATE}
          autoComplete="off"
          FieldProps={{ disabled: true }}
        />
      </Columns>
      <Text
        align="right"
        onClick={() => {
          setIsCreditCardDetailsAvailable(false);
          onOpen();
        }}
      >
        <TextLinkButton>Change credit card</TextLinkButton>
      </Text>
    </>
  );
}

export default CreditCard;
